/**
 * AQ-Cloud2 login page — SSO-only, "Option A · Centered card" design.
 *
 * Sign-in is delegated entirely to Cognito (hosted UI) via `signinRedirect`;
 * there are no local credential fields. The app attempts a silent
 * (`prompt=none`) sign-in on load (see `SilentAuthGate`), so most returning
 * users never see this page — it's the fallback when no Cognito session exists.
 *
 * States mirror design-mockups/login/option-a-centered-card.html:
 *   • idle            — welcome + "Sign in with SSO"
 *   • error           — inline alert + "Try again with SSO"
 *   • already-signed-in — success + Dashboard / Home
 * (The "checking session" state is owned by SilentAuthGate.)
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, KeyRound, Loader2, Check } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/hooks/useAppAuth'
import { AuthShell } from '@/components/layout/AuthShell'

export const LoginPage = (): JSX.Element => {
  const { isAuthenticated, isLoading, signinRedirect } = useAuth()
  const [error, setError] = React.useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = React.useState(false)

  const handleSSO = (): void => {
    setError(null)
    setIsRedirecting(true)
    signinRedirect().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
      setIsRedirecting(false)
    })
  }

  /* ── Already signed in ─────────────────────────────────────────── */
  if (isAuthenticated) {
    return (
      <AuthShell>
        <div className="w-full max-w-md">
          <Card className="shadow-lg">
            <CardContent className="p-6 sm:p-8 space-y-5 text-center">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-success/10 text-success">
                <Check className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="space-y-1">
                <h1 className="text-xl sm:text-[22px] font-semibold tracking-tight">Already signed in</h1>
                <p className="text-sm text-muted-foreground">
                  You&apos;re already signed in to your account.
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild className="flex-1 h-11">
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
                <Button asChild variant="outline" className="flex-1 h-11">
                  <Link to="/">Home</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AuthShell>
    )
  }

  /* ── Idle / error ──────────────────────────────────────────────── */
  const busy = isRedirecting || isLoading

  return (
    <AuthShell>
      <div className="w-full max-w-md space-y-4">
        <Card className="shadow-lg">
          <CardContent className="p-6 sm:p-8 space-y-5">
            <div className="text-center space-y-1">
              <h1 className="text-xl sm:text-[22px] font-semibold tracking-tight">Welcome to AutoQuotes</h1>
              <p className="text-sm text-muted-foreground">
                Sign in with your Revalize account to continue.
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Sign-in failed</AlertTitle>
                <AlertDescription>
                  We couldn&apos;t complete SSO sign-in. Please try again, or contact your
                  administrator if this continues.
                </AlertDescription>
              </Alert>
            )}

            <Button className="w-full h-11" onClick={handleSSO} disabled={busy}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <KeyRound className="h-4 w-4" aria-hidden="true" />
              )}
              {error ? 'Try again with SSO' : 'Sign in with SSO'}
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          New to AutoQuotes?{' '}
          <Link to="/register" className="text-brand-secondary hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </AuthShell>
  )
}
